import { useState } from 'react';
import { AnimatePresence, LazyMotion, m } from 'motion/react';
import { domAnimation } from '@/lib/motionFeatures';
import { Info, X } from '@alchemer/ui/icons';
import { tw } from '@/lib/tailwind';

interface MaintenanceBannerProps {
  /**
   * Optional custom message to display
   */
  message?: string;
  /**
   * Whether the banner can be dismissed
   */
  dismissible?: boolean;
  /**
   * Callback when banner is dismissed
   */
  onDismiss?: () => void;
}

/**
 * Replaces occurrences of "Thoughtspot" with "Dashboard" while preserving case
 * Only replaces standalone words, not when part of a larger word
 * @param message - The input message string
 * @returns The message with "Thoughtspot" replaced with "Dashboard"
 */
function replaceThoughtspotWithDashboard(message: string): string {
  if (!message) return '';

  // Replace standalone "Thoughtspot" with "Dashboard"
  // Replace standalone "thoughtspot" with "dashboard"
  return message.replace(/\bThoughtspot\b/g, 'Dashboard').replace(/\bthoughtspot\b/g, 'dashboard');
}

export default function MaintenanceBanner({ message, dismissible = true, onDismiss }: MaintenanceBannerProps) {
  const [isVisible, setIsVisible] = useState(true);

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  const defaultMessage = `Dashboard is in undergoing maintenance - changes won't be saved until maintenance ends.`;
  const bannerMessage = replaceThoughtspotWithDashboard(message || defaultMessage);

  return (
    <LazyMotion features={domAnimation}>
      <AnimatePresence>
        {isVisible && (
          <m.div
            role="alert"
            aria-live="polite"
            initial={{ opacity: 0, y: -100 }}
            animate={{
              opacity: 1,
              y: 0,
              transition: {
                type: 'spring',
                stiffness: 260,
                damping: 20,
              },
            }}
            exit={{
              opacity: 0,
              y: -100,
              transition: {
                type: 'spring',
                stiffness: 260,
                damping: 20,
              },
            }}
            className={tw(
              'text-foreground-800 bg-warning-500 relative flex w-full items-center justify-center overflow-hidden px-4 py-3',
            )}
          >
            <m.div
              className="flex items-center gap-2"
              aria-labelledby="maintenance-message"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{
                scale: 1,
                opacity: 1,
                transition: {
                  delay: 0.1,
                  duration: 0.2,
                  ease: [0.4, 0, 0.2, 1],
                },
              }}
              exit={{
                scale: 0.95,
                opacity: 0,
                transition: {
                  duration: 0.1,
                  ease: [0.4, 0, 1, 1],
                },
              }}
            >
              <Info
                className="h-5 w-5 shrink-0"
                aria-hidden="true"
              />
              <span id="maintenance-message">{bannerMessage}</span>
            </m.div>
            {dismissible && (
              <button
                onClick={handleDismiss}
                className={tw(
                  'hover:bg-warning-600 focus:ring-warning-800 active:bg-warning-700 absolute right-4 rounded-md p-1 transition-colors duration-150 focus:ring-2 focus:outline-none',
                )}
                aria-label="Dismiss maintenance notification"
                type="button"
              >
                <X
                  className="h-5 w-5"
                  aria-hidden="true"
                />
              </button>
            )}
          </m.div>
        )}
      </AnimatePresence>
    </LazyMotion>
  );
}
